import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiCheckCircle, HiBan, HiMail, HiDocumentText } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../components/modern";
import type { Column } from "../../components/modern";
import { hasPermission } from "../../utils/permissions";
import { useAuth } from "../../contexts/AuthContext";
import { adminApi } from "../../utils/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastNotifications";
import './UserIndex.scss';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  status: number;
  lastLogin: string;
  role: {
    id: number;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export const UserIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['users', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchUsers = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/user?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.fetchError'));
        return;
      }

      setUsers(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('users:messages.fetchError'));
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);
  const toggleStatus = async (id: number, newStatus: number) => {
    const action = newStatus === 0 ? "activate" : "ban";
    if (!window.confirm(t('users:messages.statusConfirm', { action }))) return;

    try {
      const endpoint = newStatus === 0 ? "activate" : "ban";
      const data = newStatus === 2 ? { reason: "Administrative action" } : undefined;
      const response = await adminApi.call(`/user/${endpoint}/${id}`, 'POST', data);

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.statusError'));
        return;
      }

      showSuccessToast(t('users:messages.statusSuccess', { action }));
      fetchUsers(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('users:messages.statusError'));
      console.error('Error updating user status:', error);
    }
  };

  const deleteUser = async (id: number) => {
    if (!window.confirm(t('users:messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/user/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.deleteError'));
        return;
      }

      showSuccessToast(t('users:messages.deleteSuccess'));
      fetchUsers(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('users:messages.deleteError'));
      console.error('Error deleting user:', error);
    }
  };

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (id: number) => {
    navigate(`/users/edit/${id}`);
  };

  const handleViewLogs = (id: number) => {
    navigate(`/users/logs/${id}`);
  };

  const resetPassword = async (id: number) => {
    if (!window.confirm(t('users:messages.resetPasswordConfirm'))) return;

    try {
      const response = await adminApi.call('/user/resetpassword', 'POST', { id });

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.passwordResetError'));
        return;
      }

      showSuccessToast(t('users:messages.passwordResetSuccess'));
    } catch (error) {
      showErrorToast(t('users:messages.passwordResetError'));
      console.error('Error resetting password:', error);
    }
  };
  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <span className="badge bg-success">{t('users:statusOptions.active')}</span>;
      case 1:
        return <span className="badge bg-warning">{t('users:statusOptions.inactive')}</span>;
      case 2:
        return <span className="badge bg-danger">{t('users:statusOptions.banned')}</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('users:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'email',
      title: t('users:table.email'),
      sortable: true,
      filterable: true,
      filterKey: 'email',
    },
    {
      key: 'phone',
      title: t('users:table.phone'),
      sortable: true,
      filterable: true,
      filterKey: 'phone',
    },
    {
      key: 'role',
      title: t('users:table.role'),
      sortable: true,
    },
    {
      key: 'status',
      title: t('users:table.status'),
      sortable: true,
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'actions',
      title: t('users:table.actions'),
      sortable: false,
      width: '250px',
      align: 'center',
      render: (_, user) => (
        <div className="action-buttons">
          {hasPermission("Update User") && (
            <>
              <ActionButton
                variant="warning"
                size="sm"
                icon={HiPencil}
                onClick={() => handleEditUser(user.id)}
                tooltip={t('users:editUser')}
              />
              <ActionButton
                variant={user.status === 0 ? "warning" : "success"}
                size="sm"
                icon={user.status === 0 ? HiBan : HiCheckCircle}
                onClick={() => toggleStatus(user.id, user.status === 0 ? 2 : 0)}
                tooltip={user.status === 0 ? t('users:banCustomer') : t('users:activateCustomer')}
              />
            </>
          )}
          {hasPermission("View User") && (
            <ActionButton
              variant="info"
              size="sm"
              icon={HiDocumentText}
              onClick={() => handleViewLogs(user.id)}
              tooltip={t('users:viewLogs')}
            />
          )}
          {hasPermission("Delete User") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteUser(user.id)}
              tooltip={t('users:deleteUser')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('users:breadcrumbs.users') }
  ];

  return (
    <PageLayout
      title={t('users:title')}
      subtitle={t('users:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create User") ? {
          label: t('users:addNew'),
          onClick: handleCreateUser
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchUsers(currentPage, pageSize)}
        emptyMessage={t('users:messages.noUsers')}
      />
    </PageLayout>
  );
};

